import { useState } from 'react';

import styled from '@emotion/styled';
import { ArrowDownward, Download } from '@mui/icons-material';
import {
  Alert,
  CircularProgress,
  IconButton,
  Snackbar,
  Tooltip,
} from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';

import type { GetInsuranceClaimSummaryResponse } from '../../../../../@types/contracts';
import type { Appointment } from '../../../../../react-query/useAppointments';
import { useGetLatestImagesForVisit } from '../../../../../react-query/useGetLatestImagesForVisit';
import {
  generateInsuranceClaimSummaryRequest,
  useGetInsuranceClaimSummary,
} from '../../../../../react-query/useInsuranceClaimSummary';
import { blueBlack640 } from '../../../../../theme/videa-style-tokens';
import { mixpanelEvents, useTrackEvent } from '../../../../../utils/mixpanel';
import {
  insurancePdfPathAtom,
  isInsurancePdfOpenAtom,
  patientListViewAtom,
} from '../../../atoms';

const DEFAULT_ERROR_MESSAGE =
  'Whoops! Something went wrong, please try again later or contact the Videa team for assistance.';

const StyledIconButton = styled(IconButton)`
  font-size: 1em;
  border-radius: 0.375em;
  fill: ${blueBlack640};
  padding: 0.5em;
  width: 2em;
  height: 2em;
`;

export const InsurancePDFExport = ({
  appointment,
}: {
  appointment: Appointment;
}) => {
  const setInsurancePdfOpen = useSetAtom(isInsurancePdfOpenAtom);
  const setInsurancePdfPath = useSetAtom(insurancePdfPathAtom);

  const view = useAtomValue(patientListViewAtom);

  const [errorStatus, setErrorStatus] = useState('');

  const practiceId = useSelector(
    (state: any) => state?.login?.user,
  )?.primaryPracticeId;
  const { trackEvent } = useTrackEvent();
  const getInsuranceClaimSummaryMutation = useGetInsuranceClaimSummary();

  const handleTriggerInsuranceGeneration = (imageIds: string[]) => {
    getInsuranceClaimSummaryMutation.mutate(
      generateInsuranceClaimSummaryRequest(
        appointment.patient.imagingPatientId || '',
        (
          (appointment.patient.firstName || '') +
          ' ' +
          (appointment.patient.lastName || '')
        ).trim(),
        imageIds,
        practiceId,
      ),
      {
        onSuccess: (data: GetInsuranceClaimSummaryResponse) => {
          setInsurancePdfPath(data.insuranceClaimSummaryPdfPath);
          setInsurancePdfOpen(true);
        },
        onSettled: () => {
          trackEvent(mixpanelEvents.insurancePdf, {
            patientId: appointment.patient.imagingPatientId,
            estimatedVisitId: appointment.appointment.estimatedVisitId,
            source: 'patientList',
          });
        },
        onError() {
          setErrorStatus(DEFAULT_ERROR_MESSAGE);
        },
      },
    );
  };
  const {
    isLoading,
    refetch: refetchLatestImages,
    fetchStatus,
  } = useGetLatestImagesForVisit({
    patientId: appointment.patient.imagingPatientId || '',
    visitId: appointment.appointment.estimatedVisitId || 0,
    appointmentId: appointment.appointment.pmsAppointmentId ?? undefined,
    options: {
      onSuccess: (latestImagesResult: any) => {
        if (latestImagesResult.images.length) {
          const latestImages = latestImagesResult.images;

          const imageIds = latestImages.map((image: any) => image?.imageId);
          handleTriggerInsuranceGeneration(imageIds);
        } else {
          setErrorStatus('No Images Available for this patient.');
        }
      },
      onError() {
        setErrorStatus(DEFAULT_ERROR_MESSAGE);
      },
      enabled: false,
    },
  });
  const isFetchingImages =
    (isLoading && fetchStatus !== 'idle') ||
    getInsuranceClaimSummaryMutation.isLoading;

  const triggerInsurancePdfGenerationAndView = () => {
    if (
      !isFetchingImages &&
      appointment.patient.imagingPatientId &&
      appointment.appointment.estimatedVisitId
    ) {
      refetchLatestImages();
    }
  };
  if (isFetchingImages) {
    return <CircularProgress />;
  }
  return (
    <>
      <Tooltip title="Download Insurance PDF">
        {view === 'HUDDLE' ? (
          <StyledIconButton
            aria-label="Download Insurance PDF"
            onClick={triggerInsurancePdfGenerationAndView}
          >
            <ArrowDownward />
          </StyledIconButton>
        ) : (
          <IconButton
            aria-label="Download Insurance PDF"
            onClick={triggerInsurancePdfGenerationAndView}
          >
            <Download />
          </IconButton>
        )}
      </Tooltip>
      <Snackbar
        open={!!errorStatus}
        autoHideDuration={6000}
        onClose={() => setErrorStatus('')}
      >
        <Alert severity="error">{errorStatus}</Alert>
      </Snackbar>
    </>
  );
};
